import moment from "moment-mini";
import {Engine} from "platform/engine/Engine";
import {
    ClearDealFormRanges,
    CloseDealsPayload,
    EditDealPayload,
    GoingEditDealPayload,
    GoingOpenDealPayload,
    OpenDealPayload,
    RollPositionsPayload,
    SetDealFormInProgress,
    SetDealServerError,
    SetPendingDetails,
    SetSymbolDetails,
    UpdateDealForm
} from "core/redux/deal/DealReduxActions";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import {Deal} from "platform/protocol/trading/Deal";
import {TradeType} from "protocol/trade/TradeType";
import {SetTradingModal, ToggleMenu, ToggleMenuPayload} from "core/redux/app/AppReduxActions";
import TradingModalType from "enum/TradingModalType";
import {DealForm} from "core/redux/deal/DealReduxState";
import {XhrUtil} from "core/util/XhrUtil";
import {OpenDealRequest} from "protocol/trade/OpenDealRequest";
import {Quote} from "platform/protocol/trading/Quote";
import {PositionDirection as TradeDirection} from "platform/protocol/enum/PositionDirection";
import {AccountState} from "core/state/AccountState";
import {ServiceType} from "enum/ServiceType";
import {OpenDealResponse} from "protocol/trade/OpenDealResponse";
import {HttpReject} from "platform/network/http/Http";
import {BookEntryOrderRequest} from "protocol/trade/BookEntryOrderRequest";
import Utils from "platform/util/Utils";
import {AddInboxMessage} from "core/redux/inbox/InboxReduxActions";
import InboxMessageType from "enum/InboxMessageType";
import Parameter from "platform/util/Parameter";
import InboxParameter from "enum/InboxParameter";
import {ClosePosition} from "protocol/trade/ClosePosition";
import {BaseResponse} from "protocol/BaseResponse";
import {CancelEntryOrder} from "protocol/trade/CancelEntryOrder";
import {CloseMultiplePositions} from "protocol/trade/CloseMultiplePositions";
import {PromotionType} from "platform/protocol/enum/PromotionType";
import {CloseMultiplePositionsResponse, PositionThatFailedToClose} from "protocol/trade/CloseMultiplePositionsResponse";
import {HideLoader, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {OpenPositionExtended} from "protocol/trade/OpenPositionExtended";
import {EntryOrderExtended} from "protocol/trade/EntryOrderExtended";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import DateTimeFormat from "core/format/DateTimeFormat";
import {UpdatePositionRequest} from "protocol/trade/UpdatePositionRequest";
import {UpdateEntryOrderRequest} from "protocol/trade/UpdateEntryOrderRequest";
import DealEditTarget from "enum/DealEditTarget";
import {SymbolsState} from "core/state/SymbolsState";
import WebUtil from "platform/util/WebUtil";
import {GetSymbolDetailsRequest} from "protocol/symbol/GetSymbolDetailsRequest";
import {SymbolDetails} from "protocol/symbol/GetSymbolDetailsResponse";
import {TranslationKey} from "enum/TranslationKey";
import ChartEngine from "core/engine/ChartEngine";
import {TradeUtil} from "core/util/TradeUtil";
import Translations from "platform/translation/Translations";
import {TranslationParam} from "enum/TranslationParam";
import {FetchIsRollingContractsEnabled} from "core/redux/settings/SettingsReduxActions";
import {ClosePositionsByExposureResponse} from "protocol/trade/ClosePositionsByExposureResponse";
import {ClosePositionsByExposureRequest} from "protocol/trade/ClosePositionsByExposureRequest";
import {SetFilterVisible} from "core/redux/filter/FilterReduxActions";
import {GetSymbolExpirationDateRequest} from "protocol/symbol/GetSymbolExpirationDateRequest";
import {GetSymbolExpirationDateResponse} from "protocol/symbol/GetSymbolExpirationDateResponse";
import {TradeSymbol} from "platform/protocol/trading/symbol/TradeSymbol";
import {NumberFormat} from "core/format/NumberFormat";
import {SymbolUtil} from "core/util/SymbolUtil";
import {SetClosedTradeDetails} from "core/redux/trades/ClosedTradesReduxActions";
import {LocalizationKeys} from "enum/LocalizationKeys";
import {NonProtectedTradeDeclaration} from "enum/NonProtectedTradeDeclaration";
import {AcceptNonProtectedTradingRequest} from "protocol/account/AcceptNonProtectedTradingRequest";
import AccountEngine from "core/engine/AccountEngine";
import {BIEventType} from "enum/BIEventType";
import SignalsEngine from "core/engine/SignalsEngine";
import {SetSignalsSettings} from "core/redux/deal/SignalsReduxActions";
import {PositionOpenReasonType} from "enum/PositionOpenReasonType";
import {PositionCloseReasonType} from "enum/PositionCloseReasonType";
import {UpdateOCOEntryOrderRequest} from "protocol/trade/UpdateOCOEntryOrderRequest";
import {RouteType} from "core/router/Routes";

export default class DealEngine extends Engine {

    private static _instance: DealEngine;

    public static instance(): DealEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        await SignalsEngine.instance().setup();
    }

    public onToggleMenu = (payload: ToggleMenuPayload) => {
        const {deal, symbols} = Platform.reduxState<StoreState>();
        if (deal.form && Utils.isNull(deal.form.PositionId)) {
            const {SymbolId, PositionDirection, Amount, OrderRate, RateRequestType, StopLossChecked, StopLossValueRate, StopLossValueAmount, TakeProfitChecked, TakeProfitValueRate, TakeProfitValueAmount} = deal.form;
            Platform.bi().track(payload.collapse ? BIEventType.OpenSymbolSidebar : BIEventType.CloseSymbolSidebar, {
                SymbolName: symbols.symbols.get(SymbolId)?.text,
                Direction: PositionDirection ? PositionDirection === TradeDirection.Sell ? "Sell" : "Buy" : null,
                Amount,
                OrderType: RateRequestType ? RateRequestType === TradeType.MarketOrder ? "Market order" : "Entry order" : null,
                OrderRate,
                TP: TakeProfitChecked ? TakeProfitValueRate || TakeProfitValueAmount : null,
                SL: StopLossChecked ? StopLossValueRate || StopLossValueAmount : null
            });
        }
        if (!payload.collapse) {
            const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
            symbolsState.openTradeSymbolId = null;
            setTimeout(() => {
                Platform.dispatch(ClearDealFormRanges({}));
            }, 150);
        }
    }

    public doGoingOpenDeal = async (payload: GoingOpenDealPayload) => {
        const storeState: StoreState = Platform.reduxState<StoreState>();
        const {IsUserAllowedToTrade, ShouldShowKYC, Balance, ShouldShowInappropriatePopup} = storeState.account;
        const {isSetAlert} = payload;
        if (Balance > 0) {
            if (ShouldShowInappropriatePopup) {
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.KYC,
                    info: {visible: true}
                }));
            } else if (ShouldShowKYC) {
                if (payload?.action) {
                    payload.action(RouteType.Kyc);
                } else {
                    const accountEngine: AccountEngine = Platform.engine<AccountEngine>(ServiceType.Account);
                    accountEngine.doOpenKyc();
                }
            } else if (isSetAlert || IsUserAllowedToTrade) {
                await this.doGoingOpenDealInternal(payload);
            } else {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            trKey: TranslationKey.notAllowedToTradeError
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        } else {
            await this.doGoingOpenDealInternal(payload);
        }
    }

    private doGoingOpenDealInternal = async ({SymbolId, SignalId, tradeType, Direction, orderRate = null, stopLoss = null, takeProfit = null, comments = null, action}: GoingOpenDealPayload) => {
        const storeState: StoreState = Platform.reduxState<StoreState>();
        const {ShouldAcceptNonProtectedTradeDeclaration} = storeState.account;
        const OpenFn = async () => {
            const {form} = storeState.deal;
            if (Utils.isNull(form) || Utils.greaterThen0(form.PositionId) || form.SymbolId !== SymbolId || form.SignalId !== SignalId || form.PositionDirection !== Direction
                || form.OrderRate !== orderRate || form.StopLossValueRate !== stopLoss || form.TakeProfitValueRate !== takeProfit
                || (Utils.isNotNull(tradeType) && form.RateRequestType !== tradeType) || form.Comments !== comments) {
                this._logger.debug("Going open deal for symbol id: " + SymbolId);
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                Platform.dispatch(SetPendingDetails({pending: true}));
                try {
                    const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
                    symbolsState.openTradeSymbolId = SymbolId;
                    const request: GetSymbolDetailsRequest = {SymbolId};
                    const answer: [HttpReject, SymbolDetails] = await Utils.to(this.sendToAccountService(request, "GetSymbolDetails"));
                    const Request: GetSymbolExpirationDateRequest = {symbolId: SymbolId};
                    const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                    const symbolDetails: SymbolDetails = answer[1] || {};
                    Platform.dispatch(SetSymbolDetails({details: symbolDetails}));
                    Platform.dispatch(ClearDealFormRanges({}));
                    Platform.dispatch(UpdateDealForm({
                        form: {
                            SymbolId,
                            SignalId,
                            SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                            SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                            RateRequestType: symbolDetails.DisableEntryOrders ? TradeType.MarketOrder : tradeType || TradeType.MarketOrder,
                            PositionDirection: Direction,
                            Amount: symbolDetails.Amounts?.[0],
                            Amounts: TradeUtil.openDealAmounts(symbolDetails),
                            DisableEntryOrders: symbolDetails.DisableEntryOrders,
                            OrderRate: orderRate,
                            StopLossAutomatic: Utils.isNotNull(symbolDetails.AutomaticStopLossPercentage),
                            StopLossChecked: Utils.greaterThen0(stopLoss),
                            StopLossValueRate: Utils.greaterThen0(stopLoss) ? stopLoss : null,
                            TakeProfitAutomatic: Utils.isNotNull(symbolDetails.AutomaticTakeProfitPercentage),
                            TakeProfitChecked: Utils.greaterThen0(takeProfit),
                            TakeProfitValueRate: Utils.greaterThen0(takeProfit) ? takeProfit : null,
                            Comments: comments
                        }
                    }));
                    if (action) {
                        action();
                    } else {
                        if (WebUtil.isMobile()) {
                            Platform.dispatch(SetTradingModal({
                                tradingModalType: TradingModalType.CreateEditTrade,
                                info: {
                                    visible: true
                                }
                            }));
                        } else {
                            Platform.dispatch(SetClosedTradeDetails({visible: false}));
                            Platform.dispatch(SetFilterVisible({visible: false}));
                            Platform.dispatch(SetSignalsSettings({visible: false}));
                            Platform.dispatch(ToggleMenu({collapse: true}));
                        }
                    }
                } catch (e) {
                    this._logger.debug("Failed init open trade process");
                }
                Platform.dispatch(SetPendingDetails({pending: false}));
                Platform.dispatch(HideLoader({}));
            } else {
                this._logger.debug("Opening deal for symbol id: " + SymbolId + " already in progress");
                Platform.dispatch(SetClosedTradeDetails({visible: false}));
            }
        };
        if (ShouldAcceptNonProtectedTradeDeclaration) {
            const localizationKey: LocalizationKeys = ShouldAcceptNonProtectedTradeDeclaration === NonProtectedTradeDeclaration.ShouldAcceptForAppropriate ?
                LocalizationKeys.WebProfit_Common_WebProfit_Common_NonProtectedTradingDeclaration_Appropriate : LocalizationKeys.WebProfit_Common_WebProfit_Common_NonProtectedTradingDeclaration_Inappropriate;
            Platform.dispatch(ShowPopup({
                popup: {
                    type: PopupType.INFO,
                    title: {trKey: TranslationKey.warning},
                    clazz: "non-protected-trades-declaration",
                    message: {
                        customValue: storeState.settings.localization[localizationKey]
                    },
                    showClose: true,
                    actions: [{type: PopupActionType.OK, text: {trKey: TranslationKey.accept}, action: async () => {
                            this._logger.debug("Try accept non protected trades declaration");
                            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                            const request: AcceptNonProtectedTradingRequest = {ApplicationType: 6};
                            const answer: [HttpReject, boolean] = await Utils.to(this.sendToWebProfitService(request, "AcceptNonProtectedTrading"));
                            Platform.dispatch(HideLoader({}));
                            if (answer[1]) {
                                await OpenFn();
                            }
                        }}]
                }
            }));
        } else {
            await OpenFn();
        }
    }

    public doGoingEditDeal = async (payload: GoingEditDealPayload) => {
        const {DealId, tradeType, dealEditTarget} = payload;
        const {form} = Platform.reduxState<StoreState>().deal;
        if (Utils.isNull(form) || Utils.isNull(form.PositionId) || form.PositionId !== DealId
                || (!form.StopLossChecked && dealEditTarget === DealEditTarget.StopLoss) || (!form.TakeProfitChecked && dealEditTarget === DealEditTarget.TakeProfit)) {
            this._logger.debug("Going update deal with id: " + DealId + ". Trade type: " + tradeType + " Edit target: " + dealEditTarget);
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            Platform.dispatch(SetPendingDetails({pending: true}));
            try {
                Platform.dispatch(FetchIsRollingContractsEnabled({}));
                const isMarketOrder: boolean = tradeType === TradeType.MarketOrder;
                const path: string = isMarketOrder ? "GetExtendedPosition" : "GetExtendedEntryOrder";
                const answer: [HttpReject, any] = await Utils.to(this.sendToAccountService(DealId, path));
                if (answer[0]) {
                    XhrUtil.notifyReject(answer[0]);
                } else {
                    Platform.dispatch(ClearDealFormRanges({}));
                    if (isMarketOrder) {
                        const op: OpenPositionExtended = answer[1];
                        const Request: GetSymbolExpirationDateRequest = {symbolId: op.SymbolId};
                        const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                        const request: GetSymbolDetailsRequest = {SymbolId: op.SymbolId};
                        const sAnswer: [HttpReject, SymbolDetails] = await Utils.to(this.sendToAccountService(request, "GetSymbolDetails"));
                        const symbolDetails: SymbolDetails = sAnswer[1] || {};
                        Platform.dispatch(SetSymbolDetails({details: symbolDetails}));
                        Platform.dispatch(UpdateDealForm({
                            form: {
                                SymbolId: op.SymbolId,
                                SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                                SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                                Amount: op.Amount,
                                DisableEntryOrders: symbolDetails.DisableEntryOrders,
                                PositionDirection: op.PositionDirection,
                                PositionId: op.Id,
                                RateRequestType: TradeType.MarketOrder,
                                StopLossAutomatic: Utils.isNotNull(symbolDetails.AutomaticStopLossPercentage),
                                StopLossValueRateOrigin: Utils.greaterThen0(op.StopLossRate) ? op.StopLossRate : null,
                                StopLossValueRate: Utils.greaterThen0(op.StopLossRate) ? op.StopLossRate : null,
                                StopLossValueAmountOrigin: Utils.isNotNull(op.StopLossAmount) && op.HasUserEnteredStopLossAmount ? op.StopLossAmount : null,
                                StopLossValueAmount: Utils.isNotNull(op.StopLossAmount) && op.HasUserEnteredStopLossAmount ? op.StopLossAmount : null,
                                StopLossChecked: Utils.greaterThen0(op.StopLossRate) || dealEditTarget === DealEditTarget.StopLoss,
                                TakeProfitAutomatic: Utils.isNotNull(symbolDetails.AutomaticTakeProfitPercentage),
                                TakeProfitValueRateOrigin: Utils.greaterThen0(op.TakeProfitRate) ? op.TakeProfitRate : null,
                                TakeProfitValueRate: Utils.greaterThen0(op.TakeProfitRate) ? op.TakeProfitRate : null,
                                TakeProfitValueAmountOrigin: Utils.isNotNull(op.TakeProfitAmount) && op.HasUserEnteredTakeProfitAmount ? op.TakeProfitAmount : null,
                                TakeProfitValueAmount: Utils.isNotNull(op.TakeProfitAmount) && op.HasUserEnteredTakeProfitAmount ? op.TakeProfitAmount : null,
                                TakeProfitChecked: Utils.greaterThen0(op.TakeProfitRate) || dealEditTarget === DealEditTarget.TakeProfit,
                                HasUserEnteredStopLossAmount: op.HasUserEnteredStopLossAmount,
                                HasUserEnteredTakeProfitAmount: op.HasUserEnteredTakeProfitAmount,
                                dealEditTarget
                            }
                        }));
                    } else {
                        const eo: EntryOrderExtended = answer[1];
                        let ExpirationDay: number;
                        let ExpirationMonth: number;
                        let ExpirationYear: number;
                        if (Utils.greaterThen0(eo.ExpirationDate)) {
                            const expirationDate: moment.Moment = DateTimeFormat.Of(eo.ExpirationDate);
                            ExpirationDay = expirationDate.date();
                            ExpirationMonth = expirationDate.month() + 1;
                            ExpirationYear = expirationDate.year();
                        }
                        const Request: GetSymbolExpirationDateRequest = {symbolId: eo.SymbolId};
                        const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                        const request: GetSymbolDetailsRequest = {SymbolId: eo.SymbolId};
                        const sAnswer: [HttpReject, SymbolDetails] = await Utils.to(this.sendToAccountService(request, "GetSymbolDetails"));
                        const symbolDetails: SymbolDetails = sAnswer[1] || {};
                        Platform.dispatch(SetSymbolDetails({details: symbolDetails}));
                        Platform.dispatch(UpdateDealForm({
                            form: {
                                SymbolId: eo.SymbolId,
                                SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                                SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                                Amount: eo.Amount,
                                DisableEntryOrders: symbolDetails.DisableEntryOrders,
                                OrderRateOrigin: eo.Rate,
                                OrderRate: eo.Rate,
                                PositionDirection: eo.PositionDirection,
                                PositionId: eo.Id,
                                OCOId: eo.OCOId,
                                OCORate: eo.OCORate,
                                OCOChecked: Utils.greaterThen0(eo.OCORate),
                                RateRequestType: TradeType.EntryOrder,
                                StopLossAutomatic: Utils.isNotNull(symbolDetails.AutomaticStopLossPercentage),
                                StopLossValueRateOrigin: Utils.greaterThen0(eo.StopLossRate) ? eo.StopLossRate : null,
                                StopLossValueRate: Utils.greaterThen0(eo.StopLossRate) ? eo.StopLossRate : null,
                                StopLossValueAmount: Utils.isNotNull(eo.StopLossAmount) && eo.HasUserEnteredStopLossAmount ? eo.StopLossAmount : null,
                                StopLossChecked: Utils.greaterThen0(eo.StopLossRate) || dealEditTarget === DealEditTarget.StopLoss,
                                TakeProfitAutomatic: Utils.isNotNull(symbolDetails.AutomaticTakeProfitPercentage),
                                TakeProfitValueRateOrigin: Utils.greaterThen0(eo.TakeProfitRate) ? eo.TakeProfitRate : null,
                                TakeProfitValueRate: Utils.greaterThen0(eo.TakeProfitRate) ? eo.TakeProfitRate : null,
                                TakeProfitValueAmount: Utils.isNotNull(eo.TakeProfitAmount) && eo.HasUserEnteredTakeProfitAmount ? eo.TakeProfitAmount : null,
                                TakeProfitChecked: Utils.greaterThen0(eo.TakeProfitRate) || dealEditTarget === DealEditTarget.TakeProfit,
                                ExpirationChecked: Utils.greaterThen0(eo.ExpirationDate),
                                HasUserEnteredStopLossAmount: eo.HasUserEnteredStopLossAmount,
                                HasUserEnteredTakeProfitAmount: eo.HasUserEnteredTakeProfitAmount,
                                dealEditTarget,
                                ExpirationDay,
                                ExpirationMonth,
                                ExpirationYear,
                            }
                        }));
                    }
                    if (payload.action) {
                        payload.action();
                    } else {
                        if (WebUtil.isMobile()) {
                            Platform.dispatch(SetTradingModal({
                                tradingModalType: TradingModalType.CreateEditTrade,
                                info: {
                                    visible: true
                                }
                            }));
                        } else {
                            Platform.dispatch(SetFilterVisible({visible: false}));
                            Platform.dispatch(SetSignalsSettings({visible: false}));
                            Platform.dispatch(ToggleMenu({collapse: true}));
                        }
                    }
                }
            } catch (e) {
                this._logger.debug("Failed init edit trade process");
            }
            Platform.dispatch(SetPendingDetails({pending: false}));
            Platform.dispatch(HideLoader({}));
        } else {
            this._logger.debug("Editing deal with ID: " + DealId + " already in progress");
        }
    }

    public doOpenDeal = async (payload: OpenDealPayload) => {
        const {deal, quotes, symbols} = Platform.reduxState<StoreState>();
        const form: DealForm = deal.form;
        if (form) {
            const accountState: AccountState = Platform.state(ServiceType.Account);
            const {RateRequestType, PositionDirection, SymbolId, SignalId, Amount, OrderRate, StopLossChecked, StopLossValueRate, StopLossValueAmount, TakeProfitChecked, TakeProfitValueRate, TakeProfitValueAmount, UsePositionInsurance, UseSpreadCashBack, Comments} = form;
            const quote: Quote = quotes.quotes.get(SymbolId);
            const isMarketOrder: boolean = RateRequestType === TradeType.MarketOrder;
            this._logger.debug("Ask to open deal for symbol: " + SymbolId + " Market order: " + isMarketOrder);
            Platform.bi().track(BIEventType.PositionOpening, {
                SymbolName: symbols.symbols.get(SymbolId)?.text,
                Direction: PositionDirection ? PositionDirection === TradeDirection.Sell ? "Sell" : "Buy" : null,
                Amount,
                OrderType: RateRequestType ? RateRequestType === TradeType.MarketOrder ? "Market order" : "Entry order" : null,
                OrderRate,
                TP: TakeProfitChecked ? TakeProfitValueRate || TakeProfitValueAmount : null,
                SL: StopLossChecked ? StopLossValueRate || StopLossValueAmount : null
            });
            if (isMarketOrder) {
                const PositionPromotionTypes: PromotionType[] = [];
                if (UsePositionInsurance) {
                    PositionPromotionTypes.push(PromotionType.POSITION_INSURANCE);
                }
                if (UseSpreadCashBack) {
                    PositionPromotionTypes.push(PromotionType.SPREAD_CASHBACK);
                }
                const request: OpenDealRequest = {
                    SymbolId,
                    Direction: PositionDirection,
                    Rate: PositionDirection === TradeDirection.Sell  ? quote.Bid : quote.Ask,
                    Amount: Amount.toString(),
                    Comments,
                    ExpirationDate: null,
                    OwnerAccountId: accountState.accountId.toString(),
                    PositionPromotionTypes,
                    QuoteId: quote.Id,
                    StopLossAmount: StopLossChecked && Utils.isNull(StopLossValueRate) ? StopLossValueAmount : null,
                    StopLossRate: StopLossChecked ? StopLossValueRate : null,
                    TakeProfitAmount: TakeProfitChecked && Utils.isNull(TakeProfitValueRate) ? TakeProfitValueAmount : null,
                    TakeProfitRate: TakeProfitChecked ? TakeProfitValueRate : null,
                    SignalId,
                    PositionOpenReasonType: PositionOpenReasonType.UserRequest
                };
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "OpenPosition"));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this._logger.debug("Failed open position");
                    this.setRejectReason(answer[0]);
                    Platform.bi().track(BIEventType.PositionFailed, {
                        SymbolName: symbols.symbols.get(SymbolId)?.text,
                        Direction: PositionDirection === TradeDirection.Sell  ? "Sell" : "Buy",
                        Amount,
                        OrderType: "Market order",
                        OrderRate,
                        TP: request.TakeProfitRate || request.TakeProfitAmount,
                        SL: request.StopLossRate || request.StopLossAmount
                    });
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    const response: OpenDealResponse = answer[1];
                    let openRate: number = 0;
                    if (response.P3 === 1) { // if true this is Buy and openRate is in property P33
                        openRate = response.P33;
                    } else { // else this is Sell and openRate is in property P32
                        openRate = response.P32;
                    }
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: TradingModalType.CreateEditTrade,
                        info: {
                            visible: false
                        }
                    }));
                    Platform.dispatch(AddInboxMessage({
                        message: {
                            type: InboxMessageType.TradePlaced,
                            parameters: [
                                Parameter.Of(InboxParameter.SymbolId, SymbolId),
                                Parameter.Of(InboxParameter.Direction, PositionDirection),
                                Parameter.Of(InboxParameter.Units, Amount),
                                Parameter.Of(InboxParameter.OrderRate, openRate),
                                Parameter.Of(InboxParameter.TradeType, RateRequestType)
                            ]
                        }
                    }));
                    if (payload.callback) {
                        payload.callback(response.P1);
                    }
                    Platform.bi().track(BIEventType.PositionOpened, {
                        SymbolName: symbols.symbols.get(SymbolId)?.text,
                        Direction: PositionDirection === TradeDirection.Sell  ? "Sell" : "Buy",
                        Amount,
                        OrderType: "Market order",
                        OrderRate,
                        TP: request.TakeProfitRate || request.TakeProfitAmount,
                        SL: request.StopLossRate || request.StopLossAmount
                    });
                }
            } else {
                const {ExpirationChecked, ExpirationDay, ExpirationMonth, ExpirationYear} = form;
                let ExpirationDate: string = null;
                if (ExpirationChecked) {
                    ExpirationDate = ExpirationYear + "-" + Utils.pad(ExpirationMonth) + "-" + Utils.pad(ExpirationDay);
                }
                const request: BookEntryOrderRequest = {
                    SymbolId,
                    Amount: Amount.toString(),
                    Comments,
                    EntryOrderType: null,
                    ExpirationDate,
                    LimitOrderRate: OrderRate.toString(),
                    OwnerAccountId: accountState.accountId.toString(),
                    PositionDirection,
                    StopLossAmount: StopLossChecked && Utils.isNull(StopLossValueRate) ? StopLossValueAmount : null,
                    StopLossRate: StopLossChecked ? StopLossValueRate : null,
                    TakeProfitAmount: TakeProfitChecked && Utils.isNull(TakeProfitValueRate) ? TakeProfitValueAmount : null,
                    TakeProfitRate: TakeProfitChecked ? TakeProfitValueRate : null,
                    SignalId
                };
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                // "BookOCOEntryOrders" in case we need OCO orders
                const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "BookEntryOrder"));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this._logger.debug("Failed book order");
                    this.setRejectReason(answer[0]);
                    Platform.bi().track(BIEventType.PositionFailed, {
                        SymbolName: symbols.symbols.get(SymbolId)?.text,
                        Direction: PositionDirection === TradeDirection.Sell ? "Sell" : "Buy",
                        Amount,
                        OrderType: "Entry order",
                        OrderRate,
                        TP: request.TakeProfitRate || request.TakeProfitAmount,
                        SL: request.StopLossRate || request.StopLossAmount
                    });
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    const response: OpenDealResponse = answer[1];
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: TradingModalType.CreateEditTrade,
                        info: {
                            visible: false
                        }
                    }));
                    Platform.dispatch(AddInboxMessage({
                        message: {
                            type: InboxMessageType.OrderPlaced,
                            parameters: [
                                Parameter.Of(InboxParameter.SymbolId, SymbolId),
                                Parameter.Of(InboxParameter.Direction, PositionDirection),
                                Parameter.Of(InboxParameter.Units, Amount),
                                Parameter.Of(InboxParameter.OrderRate, OrderRate),
                                Parameter.Of(InboxParameter.TradeType, RateRequestType)
                            ]
                        }
                    }));
                    if (payload.callback) {
                        payload.callback(response.P1);
                    }
                    Platform.bi().track(BIEventType.PositionOpened, {
                        SymbolName: symbols.symbols.get(SymbolId)?.text,
                        Direction: PositionDirection === TradeDirection.Sell ? "Sell" : "Buy",
                        Amount,
                        OrderType: "Entry order",
                        OrderRate,
                        TP: request.TakeProfitRate || request.TakeProfitAmount,
                        SL: request.StopLossRate || request.StopLossAmount
                    });
                }
            }
        } else {
            this._logger.debug("Can't open deal. Form absent");
        }
    }

    private setRejectReason = (error: HttpReject): void =>{
        const sError: string = XhrUtil.getRejectReason(error);
        Platform.dispatch(SetDealServerError({error: sError}));
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.ERROR,
                message: {
                    customValue: sError
                },
                showClose: true,
                icon: PopupIconType.ERROR,
                actions: [{type: PopupActionType.OK}]
            }
        }));
    }

    public doEditDeal = async (payload: EditDealPayload) => {
        const {deal, trades} = Platform.reduxState<StoreState>();
        const form: DealForm = deal.form;
        const {RateRequestType, PositionDirection, SymbolId, PositionId, Amount, OrderRate, StopLossChecked, StopLossValueRate, StopLossValueAmount,
            TakeProfitChecked, TakeProfitValueRate, TakeProfitValueAmount, HasUserEnteredStopLossAmount, HasUserEnteredTakeProfitAmount} = form;
        const isMarketOrder: boolean = RateRequestType === TradeType.MarketOrder;
        const {ExpirationChecked, ExpirationDay, ExpirationMonth, ExpirationYear} = form;
        let ExpirationDate: string = null;
        if (ExpirationChecked) {
            ExpirationDate = ExpirationYear + "-" + Utils.pad(ExpirationMonth) + "-" + Utils.pad(ExpirationDay);
        }
        const updateForm: DealForm = {};
        if (!StopLossChecked) {
            updateForm.StopLossValueRate = null;
            updateForm.StopLossValueAmount = null;
            updateForm.HasUserEnteredStopLossAmount = false;
        }
        if (!TakeProfitChecked) {
            updateForm.TakeProfitValueRate = null;
            updateForm.TakeProfitValueAmount = null;
            updateForm.HasUserEnteredTakeProfitAmount = false;
        }
        this._logger.debug("Ask to edit deal: " + PositionId + " Market order: " + isMarketOrder);
        if (isMarketOrder) {
            const request: UpdatePositionRequest = {
                Comments: null,
                ExpirationDate: null,
                PositionId,
                StopLossAmount: StopLossChecked && (HasUserEnteredStopLossAmount || Utils.isNull(StopLossValueRate)) ? StopLossValueAmount : null,
                StopLossRate: StopLossChecked && !HasUserEnteredStopLossAmount ? StopLossValueRate : null,
                TakeProfitAmount: TakeProfitChecked && (HasUserEnteredTakeProfitAmount || Utils.isNull(TakeProfitValueRate)) ? TakeProfitValueAmount : null,
                TakeProfitRate: TakeProfitChecked && !HasUserEnteredTakeProfitAmount ? TakeProfitValueRate : null
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "UpdatePosition"));
            Platform.dispatch(HideLoader({}));
            Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
            if (answer[0]) {
                this._logger.debug("Failed edit position");
                this.setRejectReason(answer[0]);
            } else {
                if (!Utils.isObjectEmpty(updateForm)) {
                    Platform.dispatch(UpdateDealForm({form: updateForm}));
                }
                const chartEngine: ChartEngine = Platform.engine(ServiceType.Chart);
                chartEngine.doUpdateDeal(PositionId, answer[1]);
                Platform.dispatch(SetDealServerError({error: null}));
                const openPosition: Deal = trades.OpenPositionsMap[PositionId];
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.CreateEditTrade,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.TradeUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId),
                            Parameter.Of(InboxParameter.Direction, PositionDirection),
                            Parameter.Of(InboxParameter.Units, Amount),
                            Parameter.Of(InboxParameter.OrderRate, openPosition?.Rate)
                        ]
                    }
                }));
                if (payload.callback) {
                    payload.callback(answer[1].P1);
                }
            }
        } else {
            const {OCOId, OCOChecked, OCORate} = form;
            let answer: [HttpReject, OpenDealResponse];
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            if (Utils.greaterThen0(OCOId)) {
                const request: UpdateOCOEntryOrderRequest = {
                    Amount,
                    Comments: null,
                    OCOEntryOrderId: OCOId,
                    ExpirationDate,
                    LimitRate: OCOChecked ? OCORate : null,
                    StopRate: OrderRate
                };
                answer = await Utils.to(this.sendToTradeService(request, "UpdateOCOEntryOrders"));
            } else {
                const request: UpdateEntryOrderRequest = {
                    Amount,
                    Comments: null,
                    ExpirationDate,
                    EntryOrderId: PositionId,
                    EntryOrderType: null,
                    LimitOrderRate: OrderRate,
                    StopLossAmount: StopLossChecked && (HasUserEnteredStopLossAmount || Utils.isNull(StopLossValueRate)) ? StopLossValueAmount : null,
                    StopLossRate: StopLossChecked && !HasUserEnteredStopLossAmount ? StopLossValueRate : null,
                    TakeProfitAmount: TakeProfitChecked && (HasUserEnteredTakeProfitAmount || Utils.isNull(TakeProfitValueRate)) ? TakeProfitValueAmount : null,
                    TakeProfitRate: TakeProfitChecked && !HasUserEnteredTakeProfitAmount ? TakeProfitValueRate : null
                };
                answer = await Utils.to(this.sendToTradeService(request, "UpdateEntryOrder"));
            }
            Platform.dispatch(HideLoader({}));
            Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
            if (answer[0]) {
                this._logger.debug("Failed update order");
                this.setRejectReason(answer[0]);
            } else {
                if (!OCOChecked) {
                    updateForm.OCORate = null;
                }
                if (!Utils.isObjectEmpty(updateForm)) {
                    Platform.dispatch(UpdateDealForm({form: updateForm}));
                }
                Platform.dispatch(SetDealServerError({error: null}));
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.CreateEditTrade,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.OrderUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId),
                            Parameter.Of(InboxParameter.Direction, PositionDirection),
                            Parameter.Of(InboxParameter.Units, Amount),
                            Parameter.Of(InboxParameter.OrderRate, OrderRate)
                        ]
                    }
                }));
                if (payload.callback) {
                    payload.callback(answer[1].P1);
                }
            }
        }
    }

    public doEditDealFromChart = async (DealId: number, StopLoss: number, TakeProfit: number): Promise<OpenDealResponse> => {
        const {OpenPositionsMap} = Platform.reduxState<StoreState>().trades;
        const deal: Deal = OpenPositionsMap[DealId];
        if (deal) {
            this._logger.debug("Start to update deal from chart " + DealId + " SL: " + StopLoss + " TP " + TakeProfit);
            const symbol: TradeSymbol = Platform.reduxState<StoreState>().symbols.symbols.get(deal.SymbolId);
            const request: UpdatePositionRequest = {
                Comments: null,
                ExpirationDate: null,
                PositionId: DealId,
                StopLossAmount: null,
                StopLossRate: NumberFormat.round(StopLoss, SymbolUtil.decimals(symbol)),
                TakeProfitAmount: null,
                TakeProfitRate: NumberFormat.round(TakeProfit, SymbolUtil.decimals(symbol))
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "UpdatePosition"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.debug("Failed edit deal from chart");
                XhrUtil.notifyReject(answer[0]);
            } else {
                this._logger.debug("Successful edit deal from chart");
                const {form} = Platform.reduxState<StoreState>().deal;
                if (form && (form.StopLossChecked || form.TakeProfitChecked)) {
                    const updatedDeal: Deal = TradeUtil.mapResponseToDeal(answer[1]);
                    Platform.dispatch(UpdateDealForm({
                        form: {
                            StopLossValueAmount: null,
                            StopLossValueRate: updatedDeal.StopLossRate,
                            TakeProfitValueAmount: null,
                            TakeProfitValueRate: updatedDeal.TakeProfitRate,
                            HasUserEnteredStopLossAmount: false,
                            HasUserEnteredTakeProfitAmount: false,
                        }
                    }));
                    const chartEngine: ChartEngine = Platform.engine(ServiceType.Chart);
                    chartEngine.doUpdateDeal(updatedDeal.Id, answer[1]);
                }
                Platform.dispatch(SetDealServerError({error: null}));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.TradeUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, deal.SymbolId),
                            Parameter.Of(InboxParameter.Direction, deal.PositionDirection),
                            Parameter.Of(InboxParameter.Units, deal.Amount),
                            Parameter.Of(InboxParameter.OrderRate, deal?.Rate)
                        ]
                    }
                }));
                return Promise.resolve(answer[1]);
            }
        } else {
            this._logger.debug("Can't update deal from chart. Deal not found: " + DealId);
        }
        return Promise.reject(false);
    }

    public doCloseDeals = async (payload: CloseDealsPayload) => {
        const {iDs, tradeType, modalType, callback} = payload;
        const {trades} = Platform.reduxState<StoreState>();
        if (Utils.isArrayNotEmpty(iDs)) {
            this._logger.debug("Try to close deals " + iDs);
            const notify = (deal: Deal, type: InboxMessageType): void => {
                Platform.dispatch(SetTradingModal({
                    tradingModalType: modalType,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, deal.SymbolId),
                            Parameter.Of(InboxParameter.Direction, deal.PositionDirection),
                            Parameter.Of(InboxParameter.Units, deal.Amount),
                            Parameter.Of(InboxParameter.OrderRate, deal.Rate)
                        ]
                    }
                }));
            };
            const isMarketOrder: boolean = tradeType === TradeType.MarketOrder;
            if (iDs.length > 1) {
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                const deals: Deal[] = [];
                const dealsMap: {[key: number]: Deal} = isMarketOrder ? trades.OpenPositionsMap : trades.EntryOrdersMap;
                iDs.forEach((id: number) => {
                    const deal: Deal = dealsMap[id];
                    if (deal) {
                        deals.push(deal);
                    }
                });
                const request: CloseMultiplePositions = {
                    PositionIds: isMarketOrder ? iDs : null,
                    EntryOrderIds: isMarketOrder ? null : iDs,
                    PositionCloseReasonType: PositionCloseReasonType.UserRequest
                };
                const path: string = isMarketOrder ? "CloseMultiplePositions" : "CancelMultipleEntryOrders";
                const answer: [HttpReject, CloseMultiplePositionsResponse] = await Utils.to(this.sendToTradeService(request, path));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this.setRejectReason(answer[0]);
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: modalType,
                        info: {
                            visible: false
                        }
                    }));
                    const response: CloseMultiplePositionsResponse = answer[1];
                    if (Utils.isArrayNotEmpty(response.PositionsThatClosedSuccessfully)) {
                        const {form} = Platform.reduxState<StoreState>().deal;
                        response.PositionsThatClosedSuccessfully.forEach((positionThatClosedSuccessfully: OpenDealResponse) => {
                            const id: number = positionThatClosedSuccessfully.P1;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                                Platform.dispatch(SetTradingModal({
                                    tradingModalType: TradingModalType.CreateEditTrade,
                                    info: {
                                        visible: false
                                    }
                                }));
                            }
                            const deal: Deal = deals.filter((d: Deal) => d.Id === id)[0];
                            if (deal) {
                                notify(deal, isMarketOrder ? InboxMessageType.TradeDeleted : InboxMessageType.OrderDeleted);
                            }
                        });
                    }
                    if (Utils.isArrayNotEmpty(response.PositionsThatFailedToClose)) {
                        const description: string[] = [Translations.text(TranslationKey.failedClosePositionDescription)];
                        response.PositionsThatFailedToClose.forEach((positionThatFailedToClose: PositionThatFailedToClose) => {
                            description.push(
                                Translations.text(TranslationKey.failedClosePosition,
                                    Parameter.Of(TranslationParam.name, positionThatFailedToClose.Item1),
                                    Parameter.Of(TranslationParam.value, positionThatFailedToClose.Item2))
                            );
                        });
                        Platform.dispatch(ShowPopup({
                            popup: {
                                type: PopupType.ERROR,
                                message: {
                                    customValue: description.join("<br />")
                                },
                                showClose: true,
                                icon: PopupIconType.ERROR,
                                actions: [{type: PopupActionType.OK}]
                            }
                        }));
                    }
                    if (callback) {
                        callback();
                    }
                }
            } else {
                if (isMarketOrder) {
                    const deal: Deal = trades.OpenPositionsMap[iDs[0]];
                    if (deal) {
                        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                        const request: ClosePosition = {
                            Comments: null,
                            PositionId: deal.Id,
                            QuoteId: deal.QuoteId,
                            Rate: deal.MarketRate,
                            PositionCloseReasonType: PositionCloseReasonType.UserRequest
                        };
                        const answer: [HttpReject, BaseResponse] = await Utils.to(this.sendToTradeService(request, "ClosePosition"));
                        Platform.dispatch(HideLoader({}));
                        Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                        if (answer[0]) {
                            this.setRejectReason(answer[0]);
                        } else {
                            const {form} = Platform.reduxState<StoreState>().deal;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === deal.Id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                                Platform.dispatch(SetTradingModal({
                                    tradingModalType: TradingModalType.CreateEditTrade,
                                    info: {
                                        visible: false
                                    }
                                }));
                            }
                            Platform.dispatch(SetDealServerError({error: null}));
                            notify(deal, InboxMessageType.TradeDeleted);
                            if (callback) {
                                callback();
                            }
                        }
                    } else {
                        this._logger.debug("Can't find deal to close with id: " + iDs[0]);
                    }
                } else {
                    const order: Deal = trades.EntryOrdersMap[iDs[0]];
                    if (order) {
                        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                        const request: CancelEntryOrder = {
                            Comments: null,
                            EntryOrderId: order.Id
                        };
                        const answer: [HttpReject, BaseResponse] = await Utils.to(this.sendToTradeService(request, "CancelEntryOrder"));
                        Platform.dispatch(HideLoader({}));
                        Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                        if (answer[0]) {
                            this.setRejectReason(answer[0]);
                        } else {
                            const {form} = Platform.reduxState<StoreState>().deal;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === order.Id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                                Platform.dispatch(SetTradingModal({
                                    tradingModalType: TradingModalType.CreateEditTrade,
                                    info: {
                                        visible: false
                                    }
                                }));
                            }
                            Platform.dispatch(SetDealServerError({error: null}));
                            notify(order, InboxMessageType.OrderDeleted);
                            if (callback) {
                                callback();
                            }
                        }
                    } else {
                        this._logger.debug("Can't find entry order to close with id: " + iDs[0]);
                    }
                }
            }
        } else {
            this._logger.debug("Can't close deals. Ids array empty");
        }
    }

    public doRollPositions = async ({SymbolId, symbol, nextSymbol}: RollPositionsPayload) => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: ClosePositionsByExposureRequest = {
            SymbolId,
            AccountId: accountState.accountId,
            Comments: null,
            IsRolling: true,
            PositionDirections: null,
            PositionCloseReasonType: PositionCloseReasonType.UserRequest
        };
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, ClosePositionsByExposureResponse] = await Utils.to(this.sendToTradeService(request, "ClosePositionsByExposure"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            this.setRejectReason(answer[0]);
        } else {
            const response: ClosePositionsByExposureResponse = answer[1];
            Platform.dispatch(ShowPopup({
                popup: {
                    type: PopupType.INFO,
                    message: {
                        trKey: response.RollingSuccess ? TranslationKey.tradeSymbolNotificationRollSuccess : TranslationKey.tradeSymbolNotificationRollFailed,
                        params: [
                            Parameter.Of(TranslationParam.name, symbol),
                            Parameter.Of(TranslationParam.value, nextSymbol)
                        ]
                    },
                    showClose: true,
                    icon: PopupIconType.INFO,
                    actions: [{type: PopupActionType.OK}]
                }
            }));
        }
    }

    private sendToWebProfitService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/WebProfitClientService.svc/json/" + path, null, true);
    }

    private sendToTradeService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "TradeServer/TradeService.svc/json/" + path);
    }

    private sendToAccountService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/" + path, null, true);
    }
}
